import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Building2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { costCentersListOptions } from "@/api/cost-centers/query-options";
import { AdminShell } from "@/components/layout/admin-shell";
import {
  IndexPageHeader,
  IndexPageHeaderTitle,
  IndexPageHeaderToolbar,
} from "@/components/vroom/index-page-header";
import { appSidebarSections, appBottomNavItems } from "@/lib/app-sidebar";
import { splitName } from "@/lib/split-name";
import { Button } from "@/components/ui/button";
import { DataTable, TableCellId } from "@/components/wrapped/data-table";
import { EmptyState } from "@/components/vroom/empty-state";

interface SessionUser {
  name: string;
  email: string;
  role?: "worker" | "approver" | "super_admin";
}

export const Route = createFileRoute("/_authed/dashboard")({
  loader: ({ context }) => context.queryClient.ensureQueryData(costCentersListOptions()),
  component: Dashboard,
});

interface CostCenterRow {
  id: string;
  code: string;
  name: string;
}

function Dashboard() {
  const { t } = useTranslation();
  const { user } = Route.useRouteContext() as { user: SessionUser };
  const { data } = useQuery(costCentersListOptions());
  const items: CostCenterRow[] = data?.data ?? [];

  const { firstName, lastName } = splitName(user.name);
  const role = user.role ?? "super_admin";

  const columns: ColumnDef<CostCenterRow, unknown>[] = [
    {
      accessorKey: "code",
      header: t("dashboard.columns.code.header"),
      cell: (info) => <TableCellId prefix="KP-" value={info.getValue<string>()} primary />,
    },
    { accessorKey: "name", header: t("dashboard.columns.name.header") },
  ];

  return (
    <AdminShell
      user={{ firstName, lastName, role }}
      section="admin"
      breadcrumb={<>{t("dashboard.breadcrumb")}</>}
      sidebarSections={appSidebarSections(
        {
          active: "cost-centers",
          role,
          costCenterCount: items.length,
        },
        t,
      )}
      bottomNavItems={appBottomNavItems({ section: "admin", active: "cost-centers", role }, t)}
    >
      <IndexPageHeader>
        <IndexPageHeaderTitle
          title={t("dashboard.title")}
          kicker={t("dashboard.kicker-total", { count: items.length })}
        />
        <IndexPageHeaderToolbar>
          <div />
          <div className="flex items-center gap-2">
            <Button variant="default">{t("common.buttons.export.label")}</Button>
            <Button variant="primary">{t("common.buttons.new.label")}</Button>
          </div>
        </IndexPageHeaderToolbar>
      </IndexPageHeader>
      <div className="px-4 py-4 md:px-6 md:py-6">
        {items.length === 0 ? (
          <EmptyState
            variant="onboarding"
            icon={<Building2 size={28} />}
            title={t("dashboard.empty.title")}
            description={t("dashboard.empty.description")}
          />
        ) : (
          <DataTable data={items} columns={columns} />
        )}
      </div>
    </AdminShell>
  );
}
